import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  addApplicationNote,
  fetchRecruitmentApplication,
  openApplicationCv,
  patchApplicationStatus,
  type ApplicationStatus,
  type RecruitmentApplication,
} from '../../../api/recruitment.api';

const PIPELINE: ApplicationStatus[] = [
  'new',
  'screening',
  'shortlisted',
  'interview',
  'final_review',
  'accepted',
  'rejected',
];

export default function RecruitmentApplicationDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [row, setRow] = useState<RecruitmentApplication | null>(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const r = await fetchRecruitmentApplication(Number(id));
      if (r.ok === false) {
        if (r.unauthorized) navigate('/admin/login', { replace: true });
        else setError(r.message);
        return;
      }
      setRow(r.data);
    })();
  }, [id, navigate]);

  const onStatus = async (status: ApplicationStatus) => {
    if (!row) return;
    setSaving(true);
    const r = await patchApplicationStatus(row.id, status);
    setSaving(false);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    setRow(r.data);
  };

  const onNote = async () => {
    if (!row || !note.trim()) return;
    setSaving(true);
    const r = await addApplicationNote(row.id, note.trim());
    setSaving(false);
    if (r.ok === false) {
      setError(r.message);
      return;
    }
    setNote('');
    setRow(r.data);
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5 max-w-5xl">
        <button type="button" onClick={() => navigate('/recruitment/applications')} className="text-xs font-bold text-teal-700 w-fit">
          ← {t('recruitment.back')}
        </button>

        {error ? <div className="rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div> : null}
        {!row ? (
          <p className="text-sm text-gray-400">{t('recruitment.loading')}</p>
        ) : (
          <>
            <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold text-gray-400" dir="ltr">
                    {row.application_number}
                  </p>
                  <h1 className="text-2xl font-black text-gray-900 mt-1">{row.full_name}</h1>
                  <p className="text-sm text-gray-500 mt-1">{row.job?.title ?? t('recruitment.applications.general')}</p>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full bg-teal-50 text-teal-700">
                  {t(`recruitment.app_status.${row.status}`)}
                </span>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5 text-sm">
                {[
                  [t('recruitment.applications.col.email'), row.email],
                  [t('recruitment.applications.col.phone'), row.phone ?? '—'],
                  [t('recruitment.applications.col.city'), row.city ?? '—'],
                  [t('recruitment.applications.col.experience'), row.years_experience != null ? `${row.years_experience}` : '—'],
                  ['LinkedIn', row.linkedin_url ?? '—'],
                  ['GitHub / Portfolio', row.portfolio_url ?? '—'],
                ].map(([k, v]) => (
                  <div key={String(k)} className="rounded-xl bg-gray-50 px-3 py-2">
                    <dt className="text-[11px] text-gray-400 font-bold">{k}</dt>
                    <dd className="text-xs font-bold text-gray-900 mt-1 break-all" dir="auto">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>

              {row.bio ? (
                <div className="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {row.bio}
                </div>
              ) : null}

              {row.has_cv ? (
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void openApplicationCv(row.id, 'view')}
                    className="px-3 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold"
                  >
                    {t('recruitment.cv.view')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void openApplicationCv(row.id, 'download')}
                    className="px-3 py-2 rounded-xl bg-white ring-1 ring-gray-200 text-xs font-bold"
                  >
                    {t('recruitment.cv.download')}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5">
              <h2 className="text-sm font-black text-gray-900 mb-3">{t('recruitment.pipeline.title')}</h2>
              <div className="flex flex-wrap gap-2">
                {PIPELINE.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={saving}
                    onClick={() => void onStatus(s)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${
                      row.status === s ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t(`recruitment.app_status.${s}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5">
                <h2 className="text-sm font-black text-gray-900 mb-3">{t('recruitment.notes.title')}</h2>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                  placeholder={t('recruitment.notes.placeholder')}
                />
                <button
                  type="button"
                  disabled={saving || !note.trim()}
                  onClick={() => void onNote()}
                  className="mt-2 px-3 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold disabled:opacity-50"
                >
                  {t('recruitment.notes.add')}
                </button>
                <div className="mt-4 space-y-3">
                  {(row.notes ?? []).map((n) => (
                    <div key={n.id} className="rounded-xl bg-gray-50 p-3">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.body}</p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        {n.author ?? '—'} · {n.created_at ? new Date(n.created_at).toLocaleString('ar-EG') : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-white ring-1 ring-gray-100 p-5">
                <h2 className="text-sm font-black text-gray-900 mb-3">{t('recruitment.timeline.title')}</h2>
                <ol className="space-y-3">
                  {(row.timeline ?? []).map((ev) => (
                    <li key={ev.id} className="border-s-2 border-teal-200 ps-3">
                      <p className="text-sm font-bold text-gray-800">{ev.label}</p>
                      <p className="text-[11px] text-gray-400">
                        {ev.actor ?? ''} {ev.created_at ? new Date(ev.created_at).toLocaleString('ar-EG') : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
